import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { invitePlayerToRegister } from '../funtionsBD/Mainservice';

const InvitePlayerDialog = ({ open, onClose, player, onInviteSent }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendInvitation = async () => {
    // Validate email
    if (!email) {
      setError(t("please_enter_email"));
      return;
    }

    if (!isValidEmail(email)) {
      setError(t("please_enter_valid_email"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await invitePlayerToRegister(player.user_id, email);

      if (response.status === 'success') {
        setSuccess(true);
        setTimeout(() => {
          onInviteSent && onInviteSent(player.user_id, email);
          handleClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError(err.message || t("error_sending_invitation"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError(null); // Clear error when user types
  };

  if (!player) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6" component="span">
              Invite Player to Register
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Player Information */}
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Player Information
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body1" fontWeight="600">
              {player.user_first_name && player.user_last_name
                ? `${player.user_first_name} ${player.user_last_name}`
                : player.user_name || 'Unknown Player'}
            </Typography>
          </Box>
          {player.fltt_classement && (
            <Chip
              label={`Classement: ${player.fltt_classement}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        {/* Instruction Text */}
        <Typography variant="body2" color="text.secondary" paragraph>
          Enter the player's email address. They will receive an invitation to complete
          their registration with their information already pre-filled.
        </Typography>

        {/* Email Input */}
        <TextField
          autoFocus
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="player@example.com"
          variant="outlined"
          disabled={loading || success}
          error={!!error}
          InputProps={{
            startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
          sx={{ mb: 2 }}
        />

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("invitation_sent_success")}
          </Alert>
        )}

        {/* Information Box */}
        <Box
          sx={{
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '16px'
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>{t("what_happens_next")}</strong>
            <br />
            • {t("invitation_info_1")}
            <br />
            • {t("invitation_info_2")}
            <br />
            • {t("invitation_info_3")}
            <br />• {t("invitation_info_4")}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSendInvitation}
          variant="contained"
          color="primary"
          disabled={loading || success || !email}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{ minHeight: 44 }}
        >
          {loading ? t("sending") : t("send_invitation")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitePlayerDialog;

